import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  defaultFilter,
  filterItems,
  getCategoryMeta,
  getItemsByType,
  isCatalogType,
  paginate,
  type FilterState,
} from "@/utils/catalog";
import FilterSidebar from "@/pages/category/components/FilterSidebar";

const PAGE_SIZE = 6;

export default function Category() {
  const { type = "" } = useParams<{ type: string }>();
  const valid = isCatalogType(type);
  const meta = getCategoryMeta(type);

  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  usePageMeta(
    valid ? `${meta.channel}_豆花视频` : "内容不存在_豆花视频",
    valid ? meta.description : "未找到该影视分类频道。"
  );

  useEffect(() => {
    setFilter(defaultFilter);
    setPage(1);
  }, [type]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 450);
    return () => clearTimeout(timer);
  }, [type, filter]);

  const filtered = useMemo(() => {
    if (!valid) return [];
    return filterItems(getItemsByType(type), filter);
  }, [type, filter, valid]);

  const paged = paginate(filtered, page, PAGE_SIZE);

  const handleReset = () => {
    setFilter(defaultFilter);
    setPage(1);
  };

  if (!valid) {
    return (
      <div className="w-full px-4 md:px-8 py-20">
        <EmptyState
          icon="ri-error-warning-line"
          title="未找到相关频道"
          description="你访问的分类不存在，返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          onAction={() => window.REACT_APP_NAVIGATE?.("/")}
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-8">
      <Breadcrumb
        items={[{ label: "首页", to: "/" }, { label: "影视分类" }, { label: meta.channel }]}
      />

      <div className="mt-5 mb-8">
        <h1 className="text-2xl md:text-3xl font-black text-foreground-950 mb-3 flex items-center gap-3">
          <span className="w-1.5 h-7 rounded-full bg-primary-500"></span>
          {meta.channel}
        </h1>
        <p className="text-sm text-foreground-500 max-w-3xl leading-relaxed">{meta.description}</p>
      </div>

      <button
        onClick={() => setMobileFilterOpen((v) => !v)}
        className="lg:hidden w-full flex items-center justify-between px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 mb-4 cursor-pointer"
      >
        <span className="flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-500"></i>
          筛选条件
        </span>
        <i className={mobileFilterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
      </button>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className={`lg:w-64 shrink-0 ${mobileFilterOpen ? "block" : "hidden lg:block"}`}>
          <div className="lg:sticky lg:top-20">
            <FilterSidebar filter={filter} onChange={(f) => { setFilter(f); setPage(1); }} onReset={handleReset} />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部
              {meta.label}
            </p>
            <p className="hidden sm:block text-xs text-foreground-400">
              第 {paged.page} / {paged.totalPages} 页
            </p>
          </div>

          <MovieGrid
            items={paged.slice}
            loading={loading}
            skeletonCount={PAGE_SIZE}
            emptyTitle="未找到相关影视内容"
            emptyDescription="换个筛选条件试试，或者重置筛选查看全部内容。"
            emptyActionLabel="重置筛选"
            onEmptyAction={handleReset}
          />

          {!loading && filtered.length > 0 && (
            <Pagination page={paged.page} totalPages={paged.totalPages} onChange={setPage} />
          )}

          <div className="mt-10 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}